import { create } from 'zustand'
import { persist } from 'zustand/middleware'

type ColumnVisibilityState = {
  hiddenColumns: Set<string>
  userHasCustomized: boolean
  toggleColumn: (columnId: string) => void
  setColumnsHidden: (columnIds: string[], hidden: boolean) => void
  showAll: () => void
  hideAll: (columnIds: string[]) => void
  isHidden: (columnId: string) => boolean
  getHiddenCount: () => number
  resetToDefaults: () => void
  removeStaleHiddenColumns: (columnIds: string[]) => void
}

export const useColumnVisibilityStore = create<ColumnVisibilityState>()(
  persist(
    (set, get) => ({
      hiddenColumns: new Set<string>(),
      userHasCustomized: false,
      toggleColumn: (columnId: string) =>
        set(state => {
          const next = new Set(state.hiddenColumns)
          if (next.has(columnId)) {
            next.delete(columnId)
          } else {
            next.add(columnId)
          }
          return { hiddenColumns: next, userHasCustomized: true }
        }),
      setColumnsHidden: (columnIds: string[], hidden: boolean) =>
        set(state => {
          const next = new Set(state.hiddenColumns)
          let changed = false
          for (const id of columnIds) {
            if (hidden) {
              if (!next.has(id)) {
                next.add(id)
                changed = true
              }
            } else if (next.has(id)) {
              next.delete(id)
              changed = true
            }
          }
          if (!changed) return state
          return { hiddenColumns: next, userHasCustomized: true }
        }),
      showAll: () =>
        set({ hiddenColumns: new Set<string>(), userHasCustomized: true }),
      hideAll: (columnIds: string[]) =>
        set(state => {
          const incoming = new Set(columnIds)
          if (
            state.hiddenColumns.size === incoming.size &&
            [...incoming].every(id => state.hiddenColumns.has(id))
          ) {
            return state
          }
          return { hiddenColumns: incoming }
        }),
      isHidden: (columnId: string) => get().hiddenColumns.has(columnId),
      getHiddenCount: () => get().hiddenColumns.size,
      resetToDefaults: () =>
        set({ hiddenColumns: new Set<string>(), userHasCustomized: false }),
      removeStaleHiddenColumns: (columnIds: string[]) =>
        set(state => {
          const next = new Set(state.hiddenColumns)
          let changed = false
          for (const id of columnIds) {
            if (next.has(id)) {
              next.delete(id)
              changed = true
            }
          }
          if (!changed) return state
          return { hiddenColumns: next }
        }),
    }),
    {
      name: 'column-visibility',
      partialize: state => ({
        hiddenColumns: Array.from(state.hiddenColumns),
        userHasCustomized: state.userHasCustomized,
      }),
      merge: (persisted, current) => {
        const p = persisted as {
          hiddenColumns?: string[]
          userHasCustomized?: boolean
        }
        const hiddenColumns = new Set(p?.hiddenColumns ?? [])
        // Migration: if userHasCustomized is missing but there are persisted
        // hiddenColumns, infer true so existing preferences aren't lost
        const userHasCustomized =
          p?.userHasCustomized ?? (hiddenColumns.size > 0 ? true : false)
        return {
          ...current,
          hiddenColumns,
          userHasCustomized,
        }
      },
    }
  )
)
